'use client';

import { createContext, useContext, useMemo } from 'react';
import { formatMessage } from '@/utils/formatMessage';

// Re-export so existing test imports (`from './TranslationContext'`) keep working
export { formatMessage };

const TranslationContext = createContext(null);

/**
 * Provides translations to all client components in the tree.
 *
 * @param {object} props
 * @param {string} props.locale   - BCP 47 locale code (e.g. 'en-GB')
 * @param {Record<string, string>} props.messages  - flat key→value translation map
 * @param {Record<string, string>} [props.fallback] - en-US messages for missing keys
 * @param {React.ReactNode} props.children
 */
export function TranslationProvider({ locale, messages, fallback = {}, children }) {
  const value = useMemo(() => {
    function t(key, values) {
      let message;
      if (messages && key in messages) {
        message = messages[key];
      } else if (fallback && key in fallback) {
        message = fallback[key];
      } else {
        message = key;
      }
      return formatMessage(locale, message, values);
    }
    return { t, locale };
  }, [locale, messages, fallback]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Returns { t, locale } from the nearest TranslationProvider.
 * Throws if called outside a provider.
 */
export function useTranslation() {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a <TranslationProvider>');
  }
  return ctx;
}
